import * as React from "react";
import { styled, useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import Divider from "@mui/material/Divider";
import PropTypes from "prop-types";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Breadcrumbs from "../common/breadcrumbs";

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}
CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const CustomTabs = styled(Tabs)(({ theme }) => ({
  "& .MuiTabs-indicator": {
    backgroundColor: theme.palette.custom.light,
  },
}));

const CustomTab = styled((props) => <Tab disableRipple {...props} />)(
  ({ theme }) => ({
    textTransform: "none",
    minWidth: 0,
    [theme.breakpoints.up("sm")]: {
      minWidth: 0,
    },
    fontWeight: theme.typography.fontWeightRegular,
    marginRight: theme.spacing(1),
    color: "rgba(0, 0, 0, 0.85)",
    fontFamily: [
      "-apple-system",
      "BlinkMacSystemFont",
      '"Segoe UI"',
      "Roboto",
      '"Helvetica Neue"',
      "Arial",
      "sans-serif",
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(","),
    "&:hover": {
      color: theme.palette.custom.light,
      opacity: 1,
    },
    "&.Mui-selected": {
      color: theme.palette.custom.light,
      fontWeight: theme.typography.fontWeightMedium,
    },
    "&.Mui-focusVisible": {
      backgroundColor: "#d1eaff",
    },
  })
);

function BasicTabs() {
  const { t } = useTranslation();
  const theme = useTheme();
  const [value, setValue] = React.useState(0);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  return (
    <Box>
      <Box
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          display: "flex",
          flexDirection: "row",
        }}
      >
        <CustomTabs
          sx={{ display: { xs: "block", sm: "flex" } }}
          value={value}
          onChange={handleChange}
          aria-label="basic tabs example"
          orientation="vertical"
        >
          <CustomTab
            sx={{
              color: `${theme.palette.custom.text}`,
              alignItems: "baseline",
            }}
            label={t("Productivity")}
            {...a11yProps(0)}
          />
          <CustomTab
            sx={{
              color: `${theme.palette.custom.text}`,
              alignItems: "baseline",
            }}
            label={t("Safety")}
            {...a11yProps(1)}
          />
          <CustomTab
            sx={{
              color: `${theme.palette.custom.text}`,
              alignItems: "baseline",
            }}
            label={t("Maintenance")}
            {...a11yProps(2)}
          />
          <CustomTab
            sx={{
              color: `${theme.palette.custom.text}`,
              alignItems: "baseline",
            }}
            label={t("Monitoring")}
            {...a11yProps(3)}
          />
          <CustomTab
            sx={{
              color: `${theme.palette.custom.text}`,
              alignItems: "baseline",
            }}
            label={t("Communication")}
            {...a11yProps(4)}
          />
        </CustomTabs>
        <CustomTabPanel
          value={value}
          index={0}
          style={{ width: "100%", marginLeft: "auto" }}
        >
          <Box sx={{ display: { xs: "block", sm: "flex" } }}>
            <Typography
              sx={{
                fontSize: "28px",
                mb: 0,
                color: theme.palette.custom.text,
                width: "100%",
                backgroundColor: theme.palette.custom.backgroundColor,
                borderRadius: "5px",
              }}
              variant="h2"
              gutterBottom
            >
              {t("PRODUCTIVITY")}:
            </Typography>
          </Box>
          <Box
            sx={{ display: { xs: "block", sm: "flex" }, marginBottom: "12px" }}
          >
            <Typography
              sx={{
                fontSize: "14.4px",
                mb: 0,
                color: theme.palette.custom.text,
              }}
              variant="h2"
              gutterBottom
            >
              <ul>
                <li>{t("Tonnage Reporting")}</li>
                <li>{t("Production/Trip Count Monitoring")}</li>
                <li>{t("Movement Reporting")}</li>
                <li>{t("Production Reporting")}</li>
                <li>{t("Digital Operator Checklists")}</li>
                <li>{t("Dispatch Messaging")}</li>
                <li>{t("Runtime/Utilization")}</li>
              </ul>

              <p>
                {t("AboutProductivityOne")}
                {t("AboutProductivityTwo")}
                {t("AboutProductivityThree")}
                {t("AboutProductivityFour")}
                {t("AboutProductivityFive")}
                {t("AboutProductivitySix")}
                {t("AboutProductivitySeven")}
              </p>
            </Typography>
          </Box>
        </CustomTabPanel>
        <CustomTabPanel
          value={value}
          index={1}
          style={{ width: "100%", marginLeft: "auto" }}
        >
          <Box sx={{ display: { xs: "block", sm: "flex" } }}>
            <Typography
              sx={{
                fontSize: "28px",
                mb: 0,
                color: theme.palette.custom.text,
                width: "100%",
                backgroundColor: theme.palette.custom.backgroundColor,
                borderRadius: "5px",
              }}
              variant="h2"
              gutterBottom
            >
              {t("SAFETY")}:
            </Typography>
          </Box>
          <Box
            sx={{ display: { xs: "block", sm: "flex" }, marginBottom: "12px" }}
          >
            <Typography
              sx={{
                fontSize: "14.4px",
                mb: 0,
                color: theme.palette.custom.text,
              }}
              variant="h2"
              gutterBottom
            >
              <p>{t("Level 9 Safety Compliance")}:</p>

              <p>
                {t("AboutSafetyOne")}
                {t("AboutSafetyTwo")}
                {t("AboutSafetyThree")}
                {t("AboutSafetyFour")}
              </p>
            </Typography>
          </Box>
        </CustomTabPanel>
        <CustomTabPanel
          value={value}
          index={2}
          style={{ width: "100%", marginLeft: "auto" }}
        >
          <Box sx={{ display: { xs: "block", sm: "flex" } }}>
            <Typography
              sx={{
                fontSize: "28px",
                mb: 0,
                color: theme.palette.custom.text,
                width: "100%",
                backgroundColor: theme.palette.custom.backgroundColor,
                borderRadius: "5px",
              }}
              variant="h2"
              gutterBottom
            >
              {t("MAINTENANCE")}:
            </Typography>
          </Box>
          <Box
            sx={{ display: { xs: "block", sm: "flex" }, marginBottom: "12px" }}
          >
            <Typography
              sx={{
                fontSize: "14.4px",
                mb: 0,
                color: theme.palette.custom.text,
              }}
              variant="h2"
              gutterBottom
            >
              <p>{t("Includes some of the following features")}:</p>

              <ul>
                <li>{t("Operational Summaries")}</li>
                <li>{t("Vehicle Speed and RPM")}</li>
                <li>{t("Engine Temperatures")}</li>
                <li>{t("Pressures")}</li>
                <li>{t("Top Offending Machines")}</li>
                <li>{t("Top Performing Machines")}</li>
              </ul>
            </Typography>
          </Box>
        </CustomTabPanel>
        <CustomTabPanel
          value={value}
          index={3}
          style={{ width: "100%", marginLeft: "auto" }}
        >
          <Box sx={{ display: { xs: "block", sm: "flex" } }}>
            <Typography
              sx={{
                fontSize: "28px",
                mb: 0,
                color: theme.palette.custom.text,
                width: "100%",
                backgroundColor: theme.palette.custom.backgroundColor,
                borderRadius: "5px",
              }}
              variant="h2"
              gutterBottom
            >
              {t("MONITORING")}:
            </Typography>
          </Box>
          <Box
            sx={{ display: { xs: "block", sm: "flex" }, marginBottom: "12px" }}
          >
            <Typography
              sx={{
                fontSize: "14.4px",
                mb: 0,
                color: theme.palette.custom.text,
              }}
              variant="h2"
              gutterBottom
            >
              <ul>
                <li>{t("Black Box Recorder")}</li>
                <li>{t("Brake Wear Monitoring")}</li>
                <li>{t("Tyre Pressure Monitoring")}</li>
                <li>{t("Engine Monitoring & Protection")}</li>
                <li>{t("Transmission Monitoring & Protection")}</li>
                <li>{t("Operational Exposure Monitoring")}</li>
                <li>{t("Temperature Monitoring")}</li>
                <li>{t("Fluid Level Monitoring")}</li>
                <li>{t("Pressure Monitoring")}</li>
                <li>{t("Flow Rate Monitoring")}</li>
              </ul>
              <p>
                {t("AboutMonitoringOne")}
                {t("AboutMonitoringTwo")}
                {t("AboutMonitoringThree")}
                {t("AboutMonitoringFour")}
                {t("AboutMonitoringFive")}
                {t("AboutMonitoringSix")}
                {t("AboutMonitoringSeven")}
              </p>
            </Typography>
          </Box>
        </CustomTabPanel>
        <CustomTabPanel
          value={value}
          index={4}
          style={{ width: "100%", marginLeft: "auto" }}
        >
          <Box sx={{ display: { xs: "block", sm: "flex" } }}>
            <Typography
              sx={{
                fontSize: "28px",
                mb: 0,
                color: theme.palette.custom.text,
                width: "100%",
                backgroundColor: theme.palette.custom.backgroundColor,
                borderRadius: "5px",
              }}
              variant="h2"
              gutterBottom
            >
              {t("COMMUNICATION")}:
            </Typography>
          </Box>
          <Box
            sx={{ display: { xs: "block", sm: "flex" }, marginBottom: "12px" }}
          >
            <Typography
              sx={{
                fontSize: "14.4px",
                mb: 0,
                color: theme.palette.custom.text,
              }}
              variant="h2"
              gutterBottom
            >
              <ul>
                <li>{t("Vehicle Area Network (VAN)")}</li>
                <li>{t("Wireless Communication Router")}</li>
              </ul>
              <p>
                {t("AboutCommunicationOne")}
                {t("AboutCommunicationTwo")}
                {t("AboutCommunicationThree")}
                {t("AboutCommunicationFour")}
                {t("AboutCommunicationFive")}
              </p>
            </Typography>
          </Box>
        </CustomTabPanel>
      </Box>
    </Box>
  );
}
const breadcrumbs = [
  { id: 0, name: "Home", href: "/" },
  { id: 1, name: "About", href: "/about" },
];

function About() {
  const theme = useTheme();
  const { t } = useTranslation();
  document.querySelector("title").innerText = "Test";
  return (
    // <Layout>
    <Box
      sx={{
        p: "12px",
        borderRadius: "16px",
        backgroundColor: theme.palette.custom.backgroundColor,
      }}
    >
      <Breadcrumbs breadcrumbs={breadcrumbs} />
      <Box sx={{ display: { xs: "block", sm: "flex" } }}>
        <Typography
          sx={{ fontSize: "45px", mb: 0, color: theme.palette.custom.text }}
          variant="h2"
          gutterBottom
        >
          {t("About")}:
        </Typography>
      </Box>
      <Divider
        sx={{ my: "10px", backgroundColor: theme.palette.custom.borderColor }}
      />
      <Box sx={{ display: { xs: "block", sm: "flex", marginBottom: "12px" } }}>
        <Typography
          sx={{ fontSize: "32px", mb: 0, color: theme.palette.custom.text }}
          variant="h2"
          gutterBottom
        >
          {t("What is SIMAC?")}
        </Typography>
      </Box>
      <Box sx={{ display: { xs: "block", sm: "flex" }, marginBottom: "12px" }}>
        <Typography
          sx={{ fontSize: "14.4px", mb: 0, color: theme.palette.custom.text }}
          variant="h2"
          gutterBottom
        >
          {t("AboutFirst")}
          {t("AboutSecond")}
          {t("AboutThird")}
        </Typography>
      </Box>
      <Box
        sx={{
          p: "12px",
          borderRadius: "16px",
          border: `1px solid ${theme.palette.custom.borderColor}`,
          marginBottom: "12px",
        }}
      >
        <Box
          sx={{ display: { xs: "block", sm: "flex", marginBottom: "12px" } }}
        >
          <Typography
            sx={{ fontSize: "32px", mb: 0, color: theme.palette.custom.text }}
            variant="h2"
            gutterBottom
          >
            {t("Dashboards")}
          </Typography>
        </Box>
        <Box
          sx={{ display: { xs: "block", sm: "flex", marginBottom: "12px" } }}
        >
          <Typography
            sx={{ fontSize: "14.4px", mb: 0, color: theme.palette.custom.text }}
            variant="h2"
            gutterBottom
          >
            {t("AboutFourth")}
            {t("AboutFifth")}
            {t("AboutSixth")}:
          </Typography>
        </Box>
        <Box
          sx={{
            display: { xs: "block", sm: "flex" },
            flexDirection: "row",
            marginBottom: "12px",
          }}
        >
          <Box
            sx={{
              p: "12px",
              borderRadius: "16px",
              border: `1px solid ${theme.palette.custom.borderColor}`,
              marginRight: "12px",
            }}
            style={{
              width: "20%",
            }}
          >
            <Box sx={{ display: { xs: "block", sm: "flex" } }}>
              <Typography
                sx={{
                  fontSize: "14.4px",
                  mb: 0,
                  color: theme.palette.custom.text,
                }}
                variant="h2"
                gutterBottom
              >
                {t("Productivity")}
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              p: "12px",
              borderRadius: "16px",
              border: `1px solid ${theme.palette.custom.borderColor}`,
              marginRight: "12px",
            }}
            style={{
              width: "20%",
            }}
          >
            <Box sx={{ display: { xs: "block", sm: "flex" } }}>
              <Typography
                sx={{
                  fontSize: "14.4px",
                  mb: 0,
                  color: theme.palette.custom.text,
                }}
                variant="h2"
                gutterBottom
              >
                {t("Safety")}
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              p: "12px",
              borderRadius: "16px",
              border: `1px solid ${theme.palette.custom.borderColor}`,
              marginRight: "12px",
            }}
            style={{
              width: "20%",
            }}
          >
            <Box sx={{ display: { xs: "block", sm: "flex" } }}>
              <Typography
                sx={{
                  fontSize: "14.4px",
                  mb: 0,
                  color: theme.palette.custom.text,
                }}
                variant="h2"
                gutterBottom
              >
                {t("Maintenance")}
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              p: "12px",
              borderRadius: "16px",
              border: `1px solid ${theme.palette.custom.borderColor}`,
              marginRight: "12px",
            }}
            style={{
              width: "20%",
            }}
          >
            <Box sx={{ display: { xs: "block", sm: "flex" } }}>
              <Typography
                sx={{
                  fontSize: "14.4px",
                  mb: 0,
                  color: theme.palette.custom.text,
                }}
                variant="h2"
                gutterBottom
              >
                {t("Monitoring")}
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              p: "12px",
              borderRadius: "16px",
              border: `1px solid ${theme.palette.custom.borderColor}`,
            }}
            style={{
              width: "20%",
            }}
          >
            <Box sx={{ display: { xs: "block", sm: "flex" } }}>
              <Typography
                sx={{
                  fontSize: "14.4px",
                  mb: 0,
                  color: theme.palette.custom.text,
                }}
                variant="h2"
                gutterBottom
              >
                {t("Communication")}
              </Typography>
            </Box>
          </Box>
        </Box>
        <BasicTabs></BasicTabs>
      </Box>
      <Box sx={{ display: { xs: "block", sm: "flex" } }}>
        <Typography
          sx={{ fontSize: "14.4px", mb: 0, color: theme.palette.custom.text }}
          variant="h2"
          gutterBottom
        >
          {t("AboutSeventh")}
          {t("AboutEight")}
          {t("AboutNinth")}
          {t("AboutTen")}
          {t("AboutEleven")}
          {t("AboutTwelve")}
          {t("AboutThirteen")}
          {t("AboutFourteen")}
          {t("The following will be available via the SIMAC dashboards")}:
        </Typography>
      </Box>
      <Box sx={{ display: { xs: "block", sm: "flex" } }}>
        <ul style={{ color: theme.palette.custom.text, fontSize: "14.4px" }}>
          <li>{t("AboutSixthteen")} </li>
          <li>{t("AboutSeventeen")} </li>
          <li>{t("AboutEighteen")} </li>
          <li>{t("AboutNineteen")} </li>
          <li>{t("AboutTwenty")}</li>
          <li>{t("AboutTwentyOne")} </li>
          <li>{t("AboutTwentyTwo")} </li>
          <li>{t("AboutTwentyThree")} </li>
          <li>{t("AboutTwentyFour")} </li>
          <li>{t("AboutTwentyFive")} </li>
          <li>{t("AboutTwentySix")} </li>
        </ul>
      </Box>
      <Box sx={{ display: { xs: "block", sm: "flex", marginBottom: "12px" } }}>
        <Typography
          sx={{ fontSize: "14.4px", mb: 0, color: theme.palette.custom.text }}
          variant="h2"
          gutterBottom
        >
          {t("AboutTwentySeven")}
          {t("AboutTwentyEight")}
          {t("AboutTwentyNine")}
          {t("AboutThirty")}
          {t("AboutThirtyOne")}
          {t("AboutThirtyTwo")}
          {t("AboutThirtythree")}
        </Typography>
      </Box>
      <Box sx={{ display: { xs: "block", sm: "flex", marginBottom: "12px" } }}>
        <Typography
          sx={{ fontSize: "28px", mb: 0, color: theme.palette.custom.text }}
          variant="h2"
          gutterBottom
        >
          {t("AboutThirtyFour")}
        </Typography>
      </Box>
      <Box sx={{ display: { xs: "block", sm: "flex", marginBottom: "12px" } }}>
        <Typography
          sx={{ fontSize: "14.4px", mb: 0, color: theme.palette.custom.text }}
          variant="h2"
          gutterBottom
        >
          {t("AboutThirtyFive")}
          {t("AboutThritySix")}
          {t("AboutThirtySeven")}
        </Typography>
      </Box>
      <Box
        sx={{
          p: "12px",
          borderRadius: "16px",
          border: `1px solid ${theme.palette.custom.borderColor}`,
        }}
      >
        <Box
          sx={{ display: { xs: "block", sm: "flex", marginBottom: "12px" } }}
        >
          <Typography
            sx={{
              fontSize: "28px",
              mb: 0,
              color: theme.palette.custom.text,
              width: "100%",
              backgroundColor: theme.palette.custom.backgroundColor,
              borderRadius: "5px",
            }}
            variant="h2"
            gutterBottom
          >
            {t("AboutThirtyEight")}
          </Typography>
        </Box>
        <Box
          sx={{ display: { xs: "block", sm: "flex", marginBottom: "12px" } }}
        >
          <Typography
            sx={{ fontSize: "14.4px", mb: 0, color: theme.palette.custom.text }}
            variant="h2"
            gutterBottom
          >
            {t("AboutThirtyNine")}
            {t("AboutFourty")}
            {t("AboutFourtyOne")}
            {t("AboutFourtyTwo")}
            {t("AboutFourtyThree")}
          </Typography>
        </Box>
        <Box
          sx={{ display: { xs: "block", sm: "flex", marginBottom: "12px" } }}
        >
          <Typography
            sx={{ fontSize: "28px", mb: 0, color: theme.palette.custom.text }}
            variant="h2"
            gutterBottom
          >
            {t("How does SBI work")}:
          </Typography>
        </Box>
        <Box sx={{ display: { xs: "block", sm: "flex" } }}>
          <Typography
            sx={{ fontSize: "14.4px", mb: 0, color: theme.palette.custom.text }}
            variant="h2"
            gutterBottom
          >
            {t("AboutFourtyFour")}
            {t("AboutFourtyFive")}
            {t("AboutFourtySix")}
            {t("AboutFourtySeven")}
            {t("AboutFourtyEight")}
          </Typography>
        </Box>
        <Box sx={{ display: { xs: "block", sm: "flex" } }}>
          <Typography
            sx={{ fontSize: "14.4px", mb: 0, color: theme.palette.custom.text }}
            variant="h2"
            gutterBottom
          >
            {t("AboutFourtyNine")}
            {t("AboutFifty")}
            {t("AboutFiftyOne")}
            {t("AboutFiftyTwo")}
            {t("AboutFiftyThree")}
            {t("AboutFiftyFour")}
            {t("AboutFiftyFive")}
          </Typography>
        </Box>
        <Box
          sx={{ display: { xs: "block", sm: "flex", marginBottom: "12px" } }}
        >
          <Typography
            sx={{ fontSize: "14.4px", mb: 0, color: theme.palette.custom.text }}
            variant="h2"
            gutterBottom
          >
            {t("AboutFiftySix")}
            {t("AboutFiftySeven")}
            {t("AboutFiftyEight")}
          </Typography>
        </Box>
        <Box
          sx={{ display: { xs: "block", sm: "flex", marginBottom: "12px" } }}
        >
          <Typography
            sx={{ fontSize: "20px", mb: 0, color: theme.palette.custom.text }}
            variant="h2"
            gutterBottom
          >
            {t("Benefits of SBI")}:
          </Typography>
        </Box>

        <Box sx={{ display: { xs: "block", sm: "flex" } }}>
          <Typography
            sx={{ fontSize: "14.4px", mb: 0, color: theme.palette.custom.text }}
            variant="h2"
            gutterBottom
          >
            {t("AboutFiftyNine")}:
            <ul>
              <li>{t("AboutSixty")} </li>
              <li>
                {t("Prevent injuries and damages due to operators speeding")}{" "}
              </li>
              <li>{t("AboutSixtyTwo")} </li>
              <li>{t("AboutSixtyThree")} </li>
              <li>{t("AboutSixtyFour")} </li>
            </ul>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default About;
